import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jobsService, type InterviewAnalysis, type Job, type TranscriptTimelineEntry } from '../services/jobs.service';

function parseTimestampLabel(label: string): number {
  const parts = label.split(':').map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return 0;
  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return (minutes * 60 + seconds) * 1000;
  }
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }
  return 0;
}

function parseTranscriptTimeline(transcript: string): TranscriptTimelineEntry[] {
  return transcript
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(
        /^\[(?<start>\d{2}:\d{2}(?::\d{2})?) - (?<end>\d{2}:\d{2}(?::\d{2})?)\] Speaker (?<speaker>[^:]+): (?<text>.+)$/,
      );
      if (!match?.groups) return null;

      return {
        speaker: match.groups.speaker,
        startMs: parseTimestampLabel(match.groups.start),
        endMs: parseTimestampLabel(match.groups.end),
        startLabel: match.groups.start,
        endLabel: match.groups.end,
        text: match.groups.text,
      } satisfies TranscriptTimelineEntry;
    })
    .filter((entry): entry is TranscriptTimelineEntry => entry !== null);
}

const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  HR: 'HR Screen',
  TECHNICAL: 'Technical',
  CULTURAL: 'Cultural Fit',
  BEHAVIORAL: 'Behavioral',
  OTHER: 'Interview',
};

function TranscriptText({
  text,
  suggestion,
}: {
  text: string;
  suggestion?: string;
}) {
  if (!suggestion) {
    return <p className="text-sm leading-relaxed text-on-surface">{text}</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm leading-relaxed text-on-surface">{text}</p>
      <div className="rounded-xl border border-primary/15 bg-background/60 px-3 py-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-primary/80">
          Suggestion
        </p>
        <p className="mt-1 text-xs italic leading-relaxed text-on-surface-variant">{suggestion}</p>
      </div>
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? 'text-green-400 border-green-400/30 bg-green-400/10'
      : score >= 60
      ? 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10'
      : 'text-error border-error/30 bg-error/10';
  return (
    <div className={`flex items-baseline gap-0.5 rounded-xl border px-3 py-1.5 font-bold tabular-nums ${color}`}>
      <span className="text-xl leading-none">{score}</span>
      <span className="text-xs opacity-70">/100</span>
    </div>
  );
}

function InterviewFeedbackContent({
  interview,
  job,
}: {
  interview: InterviewAnalysis;
  job: Job;
}) {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showTranscript, setShowTranscript] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeEntryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoadingVideo(true);
    jobsService
      .getInterviewVideoUrl(job.id, interview.id)
      .then(setVideoUrl)
      .finally(() => setLoadingVideo(false));
  }, [job.id, interview.id]);

  const timeline =
    interview.transcriptTimeline && interview.transcriptTimeline.length > 0
      ? interview.transcriptTimeline
      : parseTranscriptTimeline(interview.transcript ?? '');

  const activeIndex = timeline.findIndex(
    (entry) => entry.startMs / 1000 <= currentTime && currentTime < entry.endMs / 1000,
  );

  useEffect(() => {
    activeEntryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex]);

  const seekTo = (startMs: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startMs / 1000;
    }
  };

  const createdAt = new Date(interview.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const typeLabel = interview.interviewType
    ? (INTERVIEW_TYPE_LABELS[interview.interviewType] ?? interview.interviewType)
    : null;

  const score = interview.score ?? 0;

  return (
    <div className="flex h-[calc(100vh-3.5rem)] min-h-0 flex-col overflow-hidden bg-background md:h-screen">
      <header className="sticky top-0 z-40 border-b border-outline-variant/15 bg-background/90 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-6 lg:px-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">
              <Link to="/jobs" className="transition-colors hover:text-on-surface">Applications</Link>
              <span className="material-symbols-outlined text-[10px]">chevron_right</span>
              <Link to={`/jobs/${job.id}`} className="truncate transition-colors hover:text-on-surface">{job.company}</Link>
              <span className="material-symbols-outlined text-[10px]">chevron_right</span>
              <span className="truncate text-on-surface">Interview Feedback</span>
            </div>
            <h1 className="mt-1 truncate text-lg font-bold text-on-surface md:text-xl">{interview.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {typeLabel && (
              <span className="hidden rounded-lg border border-outline-variant/20 bg-surface-container-high px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-on-surface-variant sm:inline-flex">
                {typeLabel}
              </span>
            )}
            <ScoreBadge score={score} />
            {timeline.length > 0 && (
              <button
                onClick={() => setShowTranscript((p) => !p)}
                className={`hidden items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors xl:inline-flex ${
                  showTranscript
                    ? 'border-primary/20 bg-primary/10 text-primary hover:bg-primary/20'
                    : 'border-outline-variant/20 bg-surface-container-high text-on-surface hover:bg-surface-bright'
                }`}
              >
                <span className="material-symbols-outlined text-base">subtitles</span>
                Transcript
              </button>
            )}
            <button
              onClick={() => navigate(`/jobs/${job.id}`)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container-high px-3 py-2 text-sm font-semibold text-on-surface transition-colors hover:bg-surface-bright"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back
            </button>
          </div>
        </div>
      </header>

      <div className={`grid min-h-0 flex-1 gap-px overflow-hidden bg-outline-variant/10 transition-all ${
        showTranscript && timeline.length > 0
          ? 'grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.95fr)]'
          : 'grid-cols-1'
      }`}>
        {/* Main content */}
        <section className="min-h-0 bg-background">
          <div className="h-full overflow-y-auto px-4 py-6 md:px-6 lg:px-8">
            <div className="mx-auto flex max-w-4xl flex-col gap-6">

              {/* Video */}
              <div className="overflow-hidden rounded-2xl bg-black shadow-2xl">
                <div className="aspect-video bg-black">
                  {loadingVideo ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-white/60">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
                      <span className="text-xs">Loading video…</span>
                    </div>
                  ) : videoUrl ? (
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      controls
                      className="h-full w-full"
                      controlsList="nodownload"
                      onTimeUpdate={(e: React.SyntheticEvent<HTMLVideoElement>) =>
                        setCurrentTime(e.currentTarget.currentTime)}
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-white/40">
                      <span className="material-symbols-outlined text-4xl">videocam_off</span>
                      <span className="text-xs">Video unavailable</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-on-surface-variant">
                <span>{interview.fileName}</span>
                <span className="h-1.5 w-1.5 rounded-full bg-outline-variant/40" />
                <span>{createdAt}</span>
              </div>

              {/* Score + Summary row */}
              <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
                {/* Score card */}
                <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-outline-variant/10 bg-surface-container-low p-6 sm:min-w-[140px]">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Score</p>
                  <div className={`text-5xl font-black tabular-nums leading-none ${
                    score >= 80 ? 'text-green-400' : score >= 60 ? 'text-yellow-400' : 'text-error'
                  }`}>
                    {score}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-surface-container-highest overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        score >= 80 ? 'bg-green-400' : score >= 60 ? 'bg-yellow-400' : 'bg-error'
                      }`}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-on-surface-variant">out of 100</p>
                </div>

                {/* Summary card */}
                <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined text-base text-primary">auto_awesome</span>
                    <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">AI Summary</p>
                  </div>
                  <p className="text-sm leading-relaxed text-on-surface">{interview.summary}</p>
                </div>
              </div>

              {/* Strengths & Improvements */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Strengths */}
                <div className="rounded-2xl border border-green-400/15 bg-green-400/5 p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-400/15">
                      <span className="material-symbols-outlined text-base text-green-400" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-green-400">Strengths</p>
                  </div>
                  <ul className="space-y-2.5">
                    {interview.strengths.length > 0 ? (
                      interview.strengths.map((item, index) => (
                        <li key={index} className="flex items-start gap-2.5">
                          <span className="material-symbols-outlined mt-0.5 shrink-0 text-sm text-green-400" style={{ fontVariationSettings: "'FILL' 1" }}>check</span>
                          <span className="text-sm leading-relaxed text-on-surface">{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-on-surface-variant">No strengths extracted.</li>
                    )}
                  </ul>
                </div>

                {/* Improvements */}
                <div className="rounded-2xl border border-yellow-400/15 bg-yellow-400/5 p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-yellow-400/15">
                      <span className="material-symbols-outlined text-base text-yellow-400" style={{ fontVariationSettings: "'FILL' 1" }}>lightbulb</span>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-yellow-400">Improvements</p>
                  </div>
                  <ul className="space-y-2.5">
                    {interview.improvements.length > 0 ? (
                      interview.improvements.map((item, index) => (
                        <li key={index} className="flex items-start gap-2.5">
                          <span className="material-symbols-outlined mt-0.5 shrink-0 text-sm text-yellow-400">arrow_forward</span>
                          <span className="text-sm leading-relaxed text-on-surface">{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-on-surface-variant">No improvements extracted.</li>
                    )}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Transcript panel */}
        {showTranscript && timeline.length > 0 && (
          <aside className="min-h-0 overflow-hidden bg-surface-container-lowest">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-outline-variant/10 px-4 py-3 md:px-5">
                <p className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant/70">
                  Transcript
                </p>
                <button
                  onClick={() => setShowTranscript(false)}
                  className="rounded-md p-1 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
                  title="Close transcript"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 md:p-5">
                <div className="space-y-2">
                  {timeline.map((entry, index) => {
                    const isActive = index === activeIndex;
                    const isInterviewee =
                      entry.isInterviewee ??
                      (interview.intervieweeSpeaker
                        ? entry.speaker === interview.intervieweeSpeaker
                        : index % 2 === 0);

                    return (
                      <div
                        key={`${entry.startMs}-${index}`}
                        ref={isActive ? activeEntryRef : undefined}
                        className={`flex ${isInterviewee ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          onClick={() => seekTo(entry.startMs)}
                          className={`max-w-[92%] cursor-pointer rounded-2xl border px-3 py-2.5 transition-all ${
                            isInterviewee
                              ? 'rounded-bl-md border-primary/20 bg-primary/10'
                              : 'rounded-br-md border-outline-variant/20 bg-surface-container-high'
                          } ${isActive ? 'ring-2 ring-primary shadow-md' : 'hover:brightness-110'}`}
                        >
                          <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                            <span className={isInterviewee ? 'text-primary' : 'text-on-surface-variant'}>
                              {isInterviewee ? 'You' : `Speaker ${entry.speaker}`}
                            </span>
                            <span className="text-on-surface-variant/50">{entry.startLabel}</span>
                          </div>
                          <TranscriptText
                            text={entry.text}
                            suggestion={isInterviewee ? entry.suggestion : undefined}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

export default function InterviewFeedback() {
  const { id, interviewId } = useParams<{ id: string; interviewId: string }>();

  const { data: job, isLoading } = useQuery<Job>({
    queryKey: ['job', id],
    queryFn: () => jobsService.findOne(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center">
        <p className="text-sm text-on-surface-variant">Interview not available.</p>
      </div>
    );
  }

  const interview = job.interviews?.find((item) => item.id === interviewId);

  if (!interview) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-on-surface-variant">We couldn&apos;t find that interview for this application.</p>
        <Link
          to={`/jobs/${job.id}`}
          className="inline-flex items-center gap-2 rounded-lg bg-surface-container-high px-4 py-2 text-sm font-semibold text-on-surface transition-colors hover:bg-surface-bright"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to application
        </Link>
      </div>
    );
  }

  return <InterviewFeedbackContent interview={interview} job={job} />;
}
